import * as XLSX from 'xlsx';
import config from '../../config/config';

const getProductDataKeys = (allData, dataKeyToValueMap) => {
  if (!allData || allData.length === 0) return { keys: [], mappedHeaders: [] };
  
  const baseKeys = ['eu_pnumber', 'eu_brand_name_current', 'active_substance_current', 'eu_aut_date'];
  const keys = Object.keys(allData[0]).filter(key => !baseKeys.includes(key));
  
  const mappedHeaders = keys.map(key => {
    const niceName = dataKeyToValueMap[key] || key;
    return `"${niceName.replace(/"/g, '""')}"`; // For CSV
  });
  const excelMappedHeaders = keys.map(key => dataKeyToValueMap[key] || key); // For Excel

  return { keys, csvMappedHeaders: mappedHeaders, excelMappedHeaders };
};

const getProductDataForRow = (productData, productDataKeys) => {
  return productDataKeys.map(key => productData[key] || '');
};

const getCsvProductDataForRow = (productData, productDataKeys) => {
  return productDataKeys.map(key => {
    const value = productData[key];
    return value ? `"${value.toString().replace(/"/g, '""')}"` : '""';
  }).join(',');
};


export const exportToCSVUtility = (
  filteredAndSortedGroups,
  selectedMedicines,
  dataKeyToValueMap,
  allData,
  includeProductData,
  // groupedResults, // We get documentsMap from filteredAndSortedGroups
  displayedSearchQuery // Retained if needed for future metadata, currently unused in CSV
) => {
  const selectedItems = filteredAndSortedGroups.filter(group => selectedMedicines[group.euNumber]);
  if (selectedItems.length === 0) {
    alert('Please select at least one medicine to export');
    return;
  }

  const euNumberLabel = dataKeyToValueMap['eu_pnumber'] || 'EU Number';
  const brandNameLabel = dataKeyToValueMap['eu_brand_name_current'] || 'Brand Name';
  const activeSubstanceLabel = dataKeyToValueMap['active_substance_current'] || 'Active Substance';
  const euAuthDateLabel = dataKeyToValueMap['eu_aut_date'] || 'EU Authorization Date';

  let headers = `"${euNumberLabel}","${brandNameLabel}","${activeSubstanceLabel}","${euAuthDateLabel}","Document Count","Document Type","Document Name","Document Link","Hit Type","Image Page Number","Product Page Link"`;

  const { keys: productKeys, csvMappedHeaders } = includeProductData 
    ? getProductDataKeys(allData, dataKeyToValueMap) 
    : { keys: [], csvMappedHeaders: [] };

  if (includeProductData && csvMappedHeaders.length > 0) {
    headers += "," + csvMappedHeaders.join(',');
  }

  let csvContent = headers + "\n";

  selectedItems.forEach(item => { // item is a group from filteredAndSortedGroups
    const productPageUrl = `${window.location.origin}${config.routes.main.details}/${item.euNumber.replace(/-/g, '_')}`;
    const formattedEuNumber = item.euNumber.replace(/-/g, '/');
    const productData = allData?.find(
      data => data.eu_pnumber.replace(/-/g, '/') === formattedEuNumber // Corrected regex
    ) || {};
    const euAuthDate = productData['eu_aut_date'] || '';
    
    const docGroups = Object.values(item.documentsMap || {});

    const escapedMedicineName = `"${item.medicineName.replace(/"/g, '""')}"`;
    const escapedActiveSubstance = `"${item.activeSubstance.replace(/"/g, '""')}"`;

    if (docGroups.length === 0) { // Medicine hit, but no specific documents
      let row = `${formattedEuNumber},${escapedMedicineName},${escapedActiveSubstance},"${euAuthDate}",${item.documentCount},"","","","Text","","${productPageUrl}"`;
      if (includeProductData && productKeys.length > 0) {
        row += "," + getCsvProductDataForRow(productData, productKeys);
      }
      csvContent += row + "\n";
    } else {
      docGroups.forEach((docGroup) => { // docGroup is { details: {}, images: [] }
        const docDetails = docGroup.details;
        const docImages = docGroup.images || [];

        const fileType = docDetails.file_type === 'Nan' || !docDetails.file_type ? 'Other' : docDetails.file_type;
        const escapedDocName = docDetails.real_name ? `"${docDetails.real_name.replace(/"/g, '""')}"` : '""';
        const docLink = docDetails.file_link && docDetails.file_link !== 'Nan' ? `"${docDetails.file_link}"` : '""';

        // Row for the document itself (Text Hit)
        let docRow = `${formattedEuNumber},${escapedMedicineName},${escapedActiveSubstance},"${euAuthDate}",${item.documentCount},"${fileType}",${escapedDocName},${docLink},"Text","","${productPageUrl}"`;
        if (includeProductData && productKeys.length > 0) {
          docRow += "," + getCsvProductDataForRow(productData, productKeys);
        }
        csvContent += docRow + "\n";

        // Rows for each image hit within this document
        docImages.forEach(image => {
          const imagePageNumber = image.page !== null && image.page !== undefined ? `"${image.page}"` : '""';
          let imageRow = `${formattedEuNumber},${escapedMedicineName},${escapedActiveSubstance},"${euAuthDate}",${item.documentCount},"${fileType}",${escapedDocName},${docLink},"Image",${imagePageNumber},"${productPageUrl}"`;
          if (includeProductData && productKeys.length > 0) {
            imageRow += "," + getCsvProductDataForRow(productData, productKeys);
          }
          csvContent += imageRow + "\n";
        });
      });
    }
  });

  const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' }); // Added BOM for Excel compatibility
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `docshark-export-${new Date().toISOString().slice(0, 10)}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const exportToExcelUtility = (
  filteredAndSortedGroups,
  selectedMedicines,
  dataKeyToValueMap,
  allData,
  includeProductData,
  groupedResults,
  displayedSearchQuery
) => {
  const selectedItems = filteredAndSortedGroups.filter(group => selectedMedicines[group.euNumber]);
  if (selectedItems.length === 0) {
    alert('Please select at least one medicine to export');
    return;
  }

  const euNumberLabel = dataKeyToValueMap['eu_pnumber'] || 'EU Number';
  const brandNameLabel = dataKeyToValueMap['eu_brand_name_current'] || 'Brand Name';
  const activeSubstanceLabel = dataKeyToValueMap['active_substance_current'] || 'Active Substance';
  const euAuthDateLabel = dataKeyToValueMap['eu_aut_date'] || 'EU Authorization Date';

  const mainData = [];
  let totalUniqueDocumentsExported = 0;

  const { keys: productKeys, excelMappedHeaders } = includeProductData 
    ? getProductDataKeys(allData, dataKeyToValueMap)
    : { keys: [], excelMappedHeaders: [] };

  const headers = [
    euNumberLabel, brandNameLabel, activeSubstanceLabel, euAuthDateLabel,
    'Document Count', 'Document Type', 'Document Name', 'Document Link', 
    'Hit Type', 'Image Page Number', 'Product Page Link'
  ];

  if (includeProductData && excelMappedHeaders.length > 0) {
    headers.push(...excelMappedHeaders);
  }
  mainData.push(headers);

  const processedDocsForCounting = new Set();

  selectedItems.forEach(item => {
    const productPageUrl = `${window.location.origin}${config.routes.main.details}/${item.euNumber.replace(/-/g, '_')}`;
    const formattedEuNumber = item.euNumber.replace(/-/g, '/');
    const productData = allData?.find(
      data => data.eu_pnumber.replace(/-/g, '/') === formattedEuNumber // Corrected regex
    ) || {};
    const euAuthDate = productData['eu_aut_date'] || '';

    const docGroups = Object.values(item.documentsMap || {});

    if (docGroups.length === 0) {
      let row = [
        formattedEuNumber, item.medicineName, item.activeSubstance, euAuthDate,
        item.documentCount, '', '', '', 'Text', '', productPageUrl
      ];
      if (includeProductData && productKeys.length > 0) {
        row.push(...getProductDataForRow(productData, productKeys));
      }
      mainData.push(row);
    } else {
      docGroups.forEach(docGroup => {
        const docDetails = docGroup.details;
        const docImages = docGroup.images || [];
        const docKey = `${item.euNumber}_${docDetails.original_pdf_filename || docDetails.filename}`;
        if (!processedDocsForCounting.has(docKey)) {
            totalUniqueDocumentsExported++;
            processedDocsForCounting.add(docKey);
        }

        const fileType = docDetails.file_type === 'Nan' || !docDetails.file_type ? 'Other' : docDetails.file_type;
        const docName = docDetails.real_name || (docDetails.original_pdf_filename || docDetails.filename) || '';
        const docLink = docDetails.file_link && docDetails.file_link !== 'Nan' ? docDetails.file_link : '';

        // Row for the document itself (Text Hit)
        let docRow = [
          formattedEuNumber, item.medicineName, item.activeSubstance, euAuthDate,
          item.documentCount, fileType, docName, docLink,
          'Text', '', productPageUrl
        ];
        if (includeProductData && productKeys.length > 0) {
          docRow.push(...getProductDataForRow(productData, productKeys));
        }
        mainData.push(docRow);

        // Rows for each image hit within this document
        docImages.forEach(image => {
          const imagePageNumber = image.page !== null && image.page !== undefined ? image.page : '';
          let imageRow = [
            formattedEuNumber, item.medicineName, item.activeSubstance, euAuthDate,
            item.documentCount, fileType, docName, docLink,
            'Image', imagePageNumber, productPageUrl
          ];
          if (includeProductData && productKeys.length > 0) {
            imageRow.push(...getProductDataForRow(productData, productKeys));
          }
          mainData.push(imageRow);
        });
      });
    }
  });

  const metadataData = [
    ['DocShark Export Metadata'],
    ['Export Date', new Date().toLocaleString()],
    ['Search Query', displayedSearchQuery || 'N/A'],
    ['Total Medicines Exported', selectedItems.length],
    ['Total Unique Documents Exported', totalUniqueDocumentsExported],
    ['EMRD Product Data Included', includeProductData ? 'Yes' : 'No']
  ];

  const mainWS = XLSX.utils.aoa_to_sheet(mainData);
  const metadataWS = XLSX.utils.aoa_to_sheet(metadataData);

  // Date formatting for Excel
  Object.keys(mainWS).forEach(cellAddress => {
    if (cellAddress[0] === '!' || !mainWS[cellAddress].v) return;
    const cell = mainWS[cellAddress];
    // Check if the column is an EU Authorization Date column (index 3 in headers)
    // This is a heuristic; could be made more robust if needed
    const colIndex = XLSX.utils.decode_cell(cellAddress).c;
    if (colIndex === 3 && typeof cell.v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(cell.v)) {
        const dateParts = cell.v.split('-');
        const date = new Date(Date.UTC(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2])));
        if (!isNaN(date.getTime())) {
            cell.t = 'd';
            cell.z = 'dd/mm/yyyy'; // or 'yyyy-mm-dd'
            cell.v = date;
        }
    }
  });
  
  const mainCols = [
    { wch: 15 }, { wch: 30 }, { wch: 30 }, { wch: 20 }, { wch: 15 }, // EU Number to Document Count
    { wch: 15 }, { wch: 40 }, { wch: 50 }, // Document Type, Name, Link
    { wch: 10 }, { wch: 18 }, { wch: 50 }  // Hit Type, Image Page Number, Product Page Link
  ];
  if (includeProductData && productKeys.length > 0) {
    productKeys.forEach(() => {
      mainCols.push({ wch: 25 });
    });
  }
  mainWS['!cols'] = mainCols;

  const metadataCols = [{ wch: 30 }, { wch: 30 }];
  metadataWS['!cols'] = metadataCols;


  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, mainWS, 'Data');
  XLSX.utils.book_append_sheet(wb, metadataWS, 'Metadata');

  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `docshark-export-${new Date().toISOString().slice(0, 10)}.xlsx`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const makeEMRDselectionUtility = (
  selectedMedicines,
  checkedState,
  setCheckedState
) => {

  // 1. Create a new state object, resetting all current values to false.
  const newCheckedState = Object.fromEntries(
    Object.keys(checkedState).map(key => [key, false])
  );

  // 2. Get the keys of the medicines you want to select.
  const medicinesToSelect = Object.keys(selectedMedicines);

  // 3. Iterate over the selected medicines and set them to true.
  medicinesToSelect.forEach(key => {
    // Convert the key format from "EU-1-24-1868" to "EU/1/24/1868".
    const formattedKey = key.replaceAll('-', '/');

    // Check if the formatted key exists in our state before setting it.
    if (Object.hasOwn(newCheckedState, formattedKey)) {
      newCheckedState[formattedKey] = true;
    } else {
      console.warn(`Key "${formattedKey}" not found in checkedState.`);
    }
  });
  
  // 4. Call the state setter with the new object.
  setCheckedState(newCheckedState);
};